<template>
  <VendorFormField
    :name="name"
    :label="label"
    :required="required"
    :description="hint"
    :help="vendorHelp()"
    :error="message"
    :orientation="orientation"
    :ui="ui"
  >
    <template
      v-if="hasHelp()"
      #help
    >
      <slot name="help">{{ help }}</slot>
    </template>

    <template
      v-if="hasMessage()"
      #error
    >
      <FieldMessage :error="message">
        <slot name="help">{{ help }}</slot>
      </FieldMessage>
    </template>

    <template
      v-if="hasLabelRow()"
      #label
    >
      <slot name="label">{{ label }}</slot>
    </template>

    <template
      v-if="hasHintContent()"
      #hint
    >
      <FieldHelp
        :label="label"
        :hint="hint"
      >
        <slot name="hint" />
      </FieldHelp>
    </template>

    <template #default="control">
      <slot
        :id="control.id"
        :describedby="control.describedby"
        :invalid="control.invalid"
      />
    </template>
  </VendorFormField>
</template>

<script setup lang="ts">
import { useSlots } from 'vue'

import VendorFormField from '../../base/VendorFormField.base.vue'
import { fieldControlUi } from '../../base/fieldControlUi'
import FieldHelp from '../FieldHelp/FieldHelp.molecule.vue'
import FieldMessage from '../FieldHelp/FieldMessage.molecule.vue'
import { useFieldHelp } from '../FieldHelp/useFieldHelp'

const props = withDefaults(
  defineProps<{
    /** Field name. Nuxt UI also matches form errors against it. */
    name: string
    /** Visible label text, already translated. */
    label: string
    /** Supplementary explanation revealed by an info icon after the label. */
    hint?: string
    /** Instructions shown under the control; they stay visible while an error shows. */
    help?: string
    /** Resolved validation message; empty means valid. */
    message?: string
    /** Marks the field as mandatory. */
    required?: boolean
    /** Horizontal places the label beside the control instead of above it. */
    orientation?: 'horizontal' | 'vertical'
    /** Field slots; defaults to the shared field skin. */
    ui?: {
      container?: string
      description?: string
      error?: string
      help?: string
      hint?: string
      label?: string
      labelWrapper?: string
      root?: string
    }
  }>(),
  {
    help: '',
    hint: '',
    message: '',
    orientation: 'vertical',
    required: false,
    ui: () => fieldControlUi.formField
  }
)

defineSlots<{
  /** The control, handed the ARIA wiring the field computed for it. */
  default?: (props: { id: string; describedby: string | undefined; invalid: boolean }) => unknown
  /** Replaces the rendered label, e.g. to wrap it in a tooltip trigger. */
  label?: () => unknown
  /** Trailing content in the label row, after the info icon, e.g. a counter or state indicator. */
  hint?: () => unknown
  /** Replaces the help copy, e.g. with a status badge. */
  help?: () => unknown
}>()

const slots = useSlots()

const { hasHelp, helpBinding } = useFieldHelp(() => props.help)

const hasMessage = (): boolean => hasHelp() && props.message !== ''
// Vendor help is withheld while a message shows, so aria-describedby lists only rendered ids.
const vendorHelp = (): string => (hasMessage() ? '' : helpBinding())
const hasHintContent = (): boolean => props.hint !== '' || slots['hint'] !== undefined
const hasLabelRow = (): boolean =>
  props.label !== '' || hasHintContent() || slots['label'] !== undefined
</script>
